import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

/**
 * Cart Subscription Options
 *
 * Custom element that manages the subscription tier dropdown for cart line items.
 * Handles toggling the dropdown, selecting a new selling plan, and calling
 * /cart/change.json to switch the plan + quantity.
 *
 * Listeners are delegated from the persistent host element (not from inner
 * children) so they survive `morphSection` re-renders that replace the inner
 * toggle/option markup after a cart update.
 */
class CartSubscriptionOptions extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    this.addEventListener('click', this.#handleClick, { signal });

    document.addEventListener('click', this.#handleOutsideClick, { signal });
    document.addEventListener('keydown', this.#handleEscape, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    // #position is attached without the abort signal (the scroller is resolved
    // per open), so it has to be detached by hand or it outlives the element.
    this.#scroller?.removeEventListener('scroll', this.#position);
    window.removeEventListener('resize', this.#position);
  }

  /** @returns {HTMLElement | null} */
  get #dropdown() {
    return this.querySelector('[data-subscription-dropdown]');
  }

  /** @returns {HTMLElement | null} */
  get #toggle() {
    return this.querySelector('[data-subscription-toggle]');
  }

  /** @returns {HTMLElement | null} */
  get #chevron() {
    return this.querySelector('[data-subscription-chevron]');
  }

  /** @returns {boolean} */
  get #isOpen() {
    const dropdown = this.#dropdown;
    return dropdown ? !dropdown.hasAttribute('hidden') : false;
  }

  /**
   * The section id is not available inside the rendered cart-products snippet,
   * so the authoritative id lives on the cart-items-component ancestor.
   * @returns {string}
   */
  get #sectionId() {
    const host = this.closest('cart-items-component');
    return host?.getAttribute('data-section-id') || this.dataset.sectionId || '';
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('[data-subscription-option]');
    if (option && this.contains(option)) {
      this.#selectOption(/** @type {HTMLElement} */ (option));
      return;
    }

    // OTP upgrade: switch straight to the first subscription tier, no dropdown.
    const upgrade = target.closest('[data-subscription-upgrade]');
    if (upgrade && this.contains(upgrade)) {
      this.#selectOption(/** @type {HTMLElement} */ (upgrade));
      return;
    }

    const toggle = target.closest('[data-subscription-toggle]');
    if (toggle && this.contains(toggle)) {
      this.#toggleDropdown();
    }
  };

  #toggleDropdown() {
    if (this.#isOpen) {
      this.#close();
    } else {
      this.#open();
    }
  }

  #open() {
    const dropdown = this.#dropdown;
    dropdown?.removeAttribute('hidden');

    // Top layer, so the sticky cart footer and the overflow:auto on
    // .cart-drawer__items cannot clip or paint over the open dropdown.
    if (dropdown?.isConnected && !dropdown.matches(':popover-open')) dropdown.showPopover?.();

    this.#position();
    // The popover leaves normal flow, so nothing moves it when the item list
    // scrolls or the viewport changes — reposition it against the plan bar.
    this.#scroller?.addEventListener('scroll', this.#position, { passive: true });
    window.addEventListener('resize', this.#position, { passive: true });

    this.#chevron?.classList.add('cart-sub-options__chevron--open');
    this.#toggle?.setAttribute('aria-expanded', 'true');
  }

  #close() {
    const dropdown = this.#dropdown;

    this.#scroller?.removeEventListener('scroll', this.#position);
    window.removeEventListener('resize', this.#position);

    if (dropdown?.matches(':popover-open')) dropdown.hidePopover?.();
    dropdown?.setAttribute('hidden', '');

    this.#chevron?.classList.remove('cart-sub-options__chevron--open');
    this.#toggle?.setAttribute('aria-expanded', 'false');
  }

  /**
   * The scrolling ancestor the plan bar moves within, so an open dropdown can
   * follow it. Null on any surface that is not the drawer.
   * @returns {HTMLElement | null}
   */
  get #scroller() {
    return this.closest('.cart-drawer__items');
  }

  /**
   * Places the popover under the plan bar, flipping above it when the space
   * below is too tight. Coordinates are viewport-relative because a popover in
   * the top layer is positioned against the viewport, not against an ancestor.
   */
  #position = () => {
    const dropdown = this.#dropdown;
    const toggle = this.#toggle;
    if (!dropdown || !toggle || !dropdown.matches(':popover-open')) return;

    const GAP = 8;
    const MARGIN = 8;
    const anchor = toggle.getBoundingClientRect();
    const available = Math.max(
      0,
      Math.max(anchor.top - GAP - MARGIN, window.innerHeight - anchor.bottom - GAP - MARGIN)
    );
    dropdown.style.setProperty('--cart-sub-dropdown-max-height', `${available}px`);

    dropdown.style.left = `${anchor.left}px`;
    dropdown.style.width = `${anchor.width}px`;

    const height = dropdown.offsetHeight;
    const below = anchor.bottom + GAP;
    const fitsBelow = below + height <= window.innerHeight - MARGIN;
    dropdown.style.top = fitsBelow ? `${below}px` : `${Math.max(MARGIN, anchor.top - GAP - height)}px`;
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    if (!this.#isOpen) return;
    if (!(event.target instanceof Node)) return;
    if (!this.contains(event.target)) {
      this.#close();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleEscape = (event) => {
    if (!this.#isOpen) return;
    if (event.key === 'Escape') {
      this.#close();
    }
  };

  /**
   * Switches the line item's selling plan (and quantity) via /cart/change.json,
   * then morphs the cart section so the drawer updates automatically.
   * @param {HTMLElement} option
   */
  async #selectOption(option) {
    // No-op if this tier is already the active one — clicking the current option
    // should just close the dropdown, not fire a /cart/change.json request that
    // briefly disables the cart. (The OTP upgrade bar never has this class.)
    if (option.classList.contains('cart-items__sub-option--current')) {
      this.#close();
      return;
    }

    const sellingPlanId = option.dataset.sellingPlanId;
    const quantity = parseInt(option.dataset.quantity || '1', 10);
    const line = parseInt(this.dataset.line || '0', 10);
    const sectionId = this.#sectionId;

    if (!line || !sectionId) {
      console.error('[cart-subscription-options] Missing line or sectionId', { line, sectionId });
      return;
    }

    const cartItemsComponent = this.closest('cart-items-component');
    cartItemsComponent?.classList.add('cart-items-disabled');

    this.#close();

    try {
      const body = JSON.stringify({
        line,
        quantity,
        selling_plan: sellingPlanId ? parseInt(sellingPlanId, 10) : '',
        sections: sectionId,
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const parsedResponse = JSON.parse(await response.text());

      if (parsedResponse.errors) {
        console.error('[cart-subscription-options] API error:', parsedResponse.errors);
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponse.sections[sectionId],
        'text/html'
      );
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      document.dispatchEvent(
        new CartUpdateEvent(parsedResponse, sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-subscription-options',
          sections: parsedResponse.sections,
        })
      );

      morphSection(sectionId, parsedResponse.sections[sectionId], { mode: 'hydration' });
    } catch (error) {
      console.error('[cart-subscription-options] Error switching plan:', error);
    } finally {
      cartItemsComponent?.classList.remove('cart-items-disabled');
    }
  }
}

if (!customElements.get('cart-subscription-options')) {
  customElements.define('cart-subscription-options', CartSubscriptionOptions);
}
